import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Plus, RefreshCw, Loader2, AlertCircle, ChevronLeft, ChevronRight } from "lucide-react";
import { MultiStepAddContractModal } from "@/components/MultiStepAddContractModal";
import { toast } from "sonner";
import { Contract, ContractStatus } from "@/types";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useContracts } from "@/contexts/ContractContext";
import { useAuth } from "@/contexts/AuthContext";
import { Badge } from "@/components/ui/badge";
import { ucFirst, getAdobeStatusBadgeClass, contractNeedsReview, getStatusBadgeClass, getStatusBadgeColors } from "@/utils";
import { getAgreementsStatus } from "@/api/agreements";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";

export function Contracts() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isaddContractModalOpen, setIsaddContractModalOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();
  const { contracts, pagination, addContract, fetchContracts } = useContracts();
  const { user } = useAuth(); // Get current user

  // Check if user is a manager
  const isManager = user?.role === 'manager';

  // Auto-open modal if openModal query param is present (for manager redirect)
  useEffect(() => {
    if (searchParams.get('openModal') === 'true') {
      setIsaddContractModalOpen(true);
      // Remove the query param after opening modal
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Filter contracts based on user role
  const filteredContracts = contracts.filter(contract => {
    // For managers, hide contracts with status "pending", "active", or "complete"
    if (isManager) {
      return !(
        contract.status === ContractStatus.Pending
      );
    }
    // For admins or other roles, show all contracts
    return true;
  });

  // Load contracts with pagination on component mount
  useEffect(() => {
    fetchContracts({ size: 50, page: 1 });
  }, [fetchContracts]);

  const handleaddContract = async (newContractData: Omit<Contract, '_id'>) => {
    try {
      await addContract(newContractData);
      setIsaddContractModalOpen(false);
      await fetchContracts({ size: pagination.pageSize, page: pagination.currentPage }); // Ensure state updates
    } catch (error) {
      toast.error("Failed to add Contract");
    }
  };

  const handleViewBookings = (contractId: number) => {
    const contractData = contracts.find(c => c._id === contractId);

    // If manager and trying to view a restricted contract, show message
    if (isManager && contractData && (
      contractData.status === ContractStatus.Pending
    )) {
      toast.info("You do not have permissions for this");
      return;
    }

    navigate(`/contracts/${contractId}`, { state: { contractData } });
  };

  const handleRefreshStatus = async () => {
    try {
      setIsRefreshing(true);
      toast.info("Refreshing agreement statuses...");

      const response = await getAgreementsStatus();

      if (response && response.success) {
        await fetchContracts({ size: pagination.pageSize, page: pagination.currentPage }); // Ensure state updates
        toast.success("Agreement statuses refreshed successfully");
      } else {
        toast.error(response?.message || "Failed to refresh agreement statuses");
      }
    } catch (error: any) {
      console.error("Error refreshing agreement statuses:", error);
      toast.error(error.message || "Failed to refresh agreement statuses");
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    fetchContracts({ size: pagination.pageSize, page: 1, search: searchQuery });
  };

  const handlePageChange = (page: number) => {
    fetchContracts({ size: pagination.pageSize, page, search: searchQuery });
  };

  const handlePageSizeChange = (size: string) => {
    const newSize = parseInt(size);
    fetchContracts({ size: newSize, page: 1, search: searchQuery });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Contracts</h1>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleRefreshStatus}
            disabled={isRefreshing}
            className="flex items-center gap-2"
          >
            {isRefreshing ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Refreshing...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4" />
                Refresh Status
              </>
            )}
          </Button>
          <Button onClick={() => setIsaddContractModalOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Add Contract
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {/* Pagination Controls - Top */}
          <div className="flex items-center justify-between p-4 border-b">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                Showing {((pagination.currentPage - 1) * pagination.pageSize) + 1} to {Math.min(pagination.currentPage * pagination.pageSize, pagination.total)} of {pagination.total} contracts
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Rows per page:</span>
              <Select value={pagination.pageSize?.toString() || "50"} onValueChange={handlePageSizeChange}>
                <SelectTrigger className="w-20">
                  <SelectValue placeholder="50" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
              <form onSubmit={handleSearchSubmit} className="relative ml-4">
                <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search contracts..."
                  value={searchQuery}
                  onChange={handleSearch}
                  className="pl-8 w-[200px] h-8"
                />
              </form>
            </div>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Owner ID</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Adobe Status</TableHead>
                <TableHead>Offer Email Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredContracts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    No contracts available
                  </TableCell>
                </TableRow>
              ) : (
                filteredContracts.map((contract) => (
                  <TableRow key={contract._id}>
                    <TableCell>{`${contract.owner?.title || ''} ${contract.owner?.firstName || ''} ${contract.owner?.lastName || ''}`}</TableCell>
                    <TableCell>{contract.owner?.email || ''}</TableCell>
                    <TableCell>{contract.owner?.membershipNumber || ''}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className={getStatusBadgeClass(contract.status || 'new')}>
                          {contract.status ? ucFirst(contract.status) : 'New'}
                        </Badge>
                        {contractNeedsReview(contract) && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span>
                                  <AlertCircle className="h-4 w-4 text-amber-500" />
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Requires review</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {contract.adobeStatus ? (
                        <Badge variant="secondary" className={getAdobeStatusBadgeClass(contract.adobeStatus)}>
                          {ucFirst(contract.adobeStatus.replace(/_/g, ' '))}
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="bg-gray-100 text-gray-800">
                          None
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {contract.welcomeEmailStatus ? (
<Badge
                          className={
                            contract.welcomeEmailStatus === "sent"
                              ? "bg-green-100 text-green-800"
                              : contract.welcomeEmailStatus === "pending"
                                ? "bg-yellow-100 text-yellow-800"
                                : contract.welcomeEmailStatus === "failed"
                                  ? "bg-red-100 text-red-800"
                                  : "bg-gray-100 text-gray-800"
                          }
                        >
                          {ucFirst(contract.welcomeEmailStatus)}
                        </Badge>
                      ) : (
                        <Badge className="bg-gray-100 text-gray-800">None</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" onClick={() => handleViewBookings(contract._id)}>
                        View Details
                      </Button>
                      <Link to="/calendar">
                        <Button variant="ghost" size="sm">
                          Add Booking
                        </Button>
                      </Link>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {/* Pagination Controls - Bottom */}
          <div className="flex items-center justify-between p-4 border-t">
            <div className="text-sm text-muted-foreground">
              Page {pagination.currentPage} of {pagination.totalPages}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={pagination.currentPage <= 1}
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={pagination.currentPage >= pagination.totalPages}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <MultiStepAddContractModal
        isOpen={isaddContractModalOpen}
        onClose={() => setIsaddContractModalOpen(false)}
        onSubmit={handleaddContract}
      />
    </div>
  );
}